import { useForm } from 'react-hook-form';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { toast } from 'react-toastify';

import {
  AgentDto,
  AgentRequestDto,
  SquadAgentResponseDto,
  useSquadAgentCreateControllerExecuteMutation,
  useSquadAgentUpdateControllerExecuteMutation,
  useCampaignAgentCreateControllerExecuteMutation,
  useCampaignAgentUpdateControllerExecuteMutation,
} from '@shared/services/apiService/apiService';
import { CreateAgentFormValues } from '../model/types';
import { ErrorData } from '@shared/types/admin/agent';
import { DEFAULT_NO_ROLE_AGENT_PROFILE } from '@pages/Admin/Main/Agent/ui/components/ManageAgent/constants';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { CustomErrorToast } from '@shared/components/customErrorToast';
import { t } from 'i18next';

const isSquadAgent = (agent: AgentDto | SquadAgentResponseDto): agent is SquadAgentResponseDto =>
  'isDefault' in agent;

export const useCreateAgent = (
  campaignId: string,
  agents: (AgentDto | SquadAgentResponseDto)[],
  squadId?: string | null,
) => {
  const [createSquadAgent, { isLoading: isSquadAgentLoading }] =
    useSquadAgentCreateControllerExecuteMutation();
  const [updateSquadAgent, { isLoading: isSquadAgentUpdateLoading }] =
    useSquadAgentUpdateControllerExecuteMutation();
  const [createCampaignAgent, { isLoading: isCampaignAgentCreating }] =
    useCampaignAgentCreateControllerExecuteMutation();
  const [updateCampaignAgent, { isLoading: isCampaignAgentUpdating }] =
    useCampaignAgentUpdateControllerExecuteMutation();
  const isLoading =
    isSquadAgentLoading ||
    isSquadAgentUpdateLoading ||
    isCampaignAgentCreating ||
    isCampaignAgentUpdating;

  const { handleSubmit, setValue, reset, ...formMethods } = useForm<CreateAgentFormValues>({
    mode: 'all',
    defaultValues: {
      id: '',
      name: '',
      voiceId: '',
      // LLM defaults
      llmProviderId: undefined,
      llmModelId: null,
      llmCustomModelKey: null,
    },
  });

  function handleErrResponse(error: FetchBaseQueryError | SerializedError) {
    if ('data' in error && error.data) {
      const errorData = error.data as ErrorData;
      toast.error((props) => <CustomErrorToast {...props} message={errorData.message} />, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
      });
      return;
    }
    toast.error((props) => <CustomErrorToast {...props} message={t('common.errorMessage')} />, {
      closeButton: false,
      autoClose: 5000,
      icon: false,
    });
  }

  const saveAgent = async (data: CreateAgentFormValues): Promise<boolean> => {
    try {
      const existingAgent = data.id ? agents.find((a) => a.id === data.id) : null;

      const agentData = {
        name: data.name,
        voiceId: data.voiceId!,
        transcriberId: data.transcriberId!,
        profileId: data.profile?.id !== DEFAULT_NO_ROLE_AGENT_PROFILE.id ? data.profile?.id : null,
        languages: data.languages,
        // LLM fields come directly from the form. One of llmModelId or llmCustomModelKey must be provided
        // depending on provider.allowsCustomModels. The UI ensures this.
        llmProviderId: data.llmProviderId!,
        llmModelId: data.llmModelId ?? null,
        llmCustomModelKey: data.llmCustomModelKey ?? null,
        ...(existingAgent?.tools ? { tools: existingAgent.tools } : {}),
      } as any;

      let response;

      // Squad mode
      if (squadId) {
        if (data.id) {
          // Editing existing agent in squad: use squad agent update endpoint
          response = await updateSquadAgent({
            id: squadId,
            agentId: data.id,
            squadAgentUpdateRequestDto: {
              // LLM config is required by backend for update
              llmProviderId: agentData.llmProviderId,
              llmModelId: agentData.llmModelId,
              llmCustomModelKey: agentData.llmCustomModelKey,
              name: agentData.name,
              voiceId: agentData.voiceId,
              transcriberId: agentData.transcriberId,
              profileId: agentData.profileId,
              languages: agentData.languages,
              isDefault:
                existingAgent && isSquadAgent(existingAgent) ? existingAgent.isDefault : false,
            },
          });
        } else {
          // Creating a new agent in squad mode
          response = await createSquadAgent({
            id: squadId,
            agentRequestDto: {
              // LLM config is required on create
              llmProviderId: agentData.llmProviderId,
              llmModelId: agentData.llmModelId,
              llmCustomModelKey: agentData.llmCustomModelKey,
              name: agentData.name,
              voiceId: agentData.voiceId,
              transcriberId: agentData.transcriberId,
              profileId: agentData.profileId,
              languages: agentData.languages,
            } as AgentRequestDto,
          });
        }
      } else {
        // Campaign mode (non-squad): use v2 campaign agent endpoints
        if (data.id) {
          // Editing existing agent in campaign
          response = await updateCampaignAgent({
            id: campaignId,
            agentId: data.id,
            // API expects agentRequestDto for update as well
            agentRequestDto: {
              // LLM config is required on update
              llmProviderId: agentData.llmProviderId,
              llmModelId: agentData.llmModelId,
              llmCustomModelKey: agentData.llmCustomModelKey,
              name: agentData.name,
              voiceId: agentData.voiceId,
              transcriberId: agentData.transcriberId,
              profileId: agentData.profileId,
              languages: agentData.languages,
            },
          });
        } else {
          // Creating new agent in campaign
          response = await createCampaignAgent({
            id: campaignId,
            agentRequestDto: {
              // LLM config is required on create
              llmProviderId: agentData.llmProviderId,
              llmModelId: agentData.llmModelId,
              llmCustomModelKey: agentData.llmCustomModelKey,
              name: agentData.name,
              voiceId: agentData.voiceId,
              transcriberId: agentData.transcriberId,
              profileId: agentData.profileId,
              languages: agentData.languages,
            } as AgentRequestDto,
          });
        }
      }

      if ('error' in response) {
        const { error } = response;
        if (error) {
          handleErrResponse(error);
        }
        return false;
      }

      if ('data' in response) {
        toast.success(CustomSuccessToast, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
          data: {
            title: data.id ? t('agentPage.agent.editedTitle') : t('agentPage.agent.savedTitle'),
            description: data.id
              ? t('agentPage.agent.editedDescription', { name: data.name })
              : t('agentPage.agent.savedDescription', { name: data.name }),
          },
        });
        return true;
      }

      return false;
    } catch (error) {
      console.log('Creating agent failed:', error);
      toast.error((props) => <CustomErrorToast {...props} message={t('common.errorMessage')} />, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
      });
      return false;
    }
  };

  const onSubmit = (): Promise<boolean> =>
    new Promise((resolve) => {
      handleSubmit(
        async (data) => resolve(await saveAgent(data)),
        () => resolve(false),
      )();
    });

  return {
    setValue,
    reset,
    onSubmit,
    isLoading,
    ...formMethods,
  };
};
